import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Container, Typography } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import DocStyleContent from "./doc-style-content";

export default function Doc({ docName = "" }) {
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTerms = async () => {
      setIsLoading(true);
      setError("");
      setContent("");
      try {
        const targetUrl = `https://mhome-legal.s3.us-east-1.amazonaws.com/${docName}.md`;
        const response = await fetch(`${targetUrl}`, {
          method: "GET",
          headers: {
            Accept: "text/markdown,text/plain,*/*",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch terms of use");
        }
        const text = await response.text();
        if (text) {
          setContent(text);
        } else {
          throw new Error("No content received");
        }
      } catch (err) {
        console.error("Error fetching terms:", err);
        setError("Failed to load privacy policy. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (docName) {
      fetchTerms();
    } else {
      setIsLoading(false);
      setError("Document name is required");
    }
  }, [docName]);

  if (isLoading) {
    return (
      <div
        className="flex justify-center items-center"
        style={{
          minHeight: "100vh",
        }}
      >
        <CircularProgress size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex justify-center items-center"
        style={{
          minHeight: "100vh",
          color: "hsl(var(--destructive))",
        }}
      >
        <div className="text-center">
          <Typography variant="h6" style={{ marginBottom: "16px" }}>
            {error}
          </Typography>
          <Typography
            component="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              color: "hsl(var(--primary))",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            Try Again
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <Container
      maxWidth={false}
      style={{
        backgroundColor: "hsl(var(--card))",
        minHeight: "100vh",
        paddingTop: "48px",
        paddingBottom: "48px",
      }}
    >
      <DocStyleContent>
        <ReactMarkdown
          components={{
            h1: ({ node, ...props }) => (
              <Typography variant="h1" component="h1" {...props} />
            ),
            h2: ({ node, ...props }) => (
              <Typography variant="h2" component="h2" {...props} />
            ),
            p: ({ node, ...props }) => <Typography component="p" {...props} />,
            ul: ({ node, ...props }) => <ul {...props} />,
            ol: ({ node, ...props }) => <ol {...props} />,
            li: ({ node, ...props }) => <li {...props} />,
          }}
        >
          {content}
        </ReactMarkdown>
      </DocStyleContent>
    </Container>
  );
}
